
import { Options, Vue } from 'vue-class-component';
import EventBusService from "@/services/EventBusService";
import CourseService from "@/services/CourseService";
import RegionService from "@/services/RegionService";
import { Course, CourseEnrollment } from "@/data/course";
import CourseActions from "@/components/CourseActions.vue";
import CourseSpecialInfo from "@/components/CourseSpecialInfo.vue";
import RegionCoursesCard from "@/components/RegionCoursesCard.vue";
import CourseFilter from "@/components/filters/CourseFilter.vue";
import { createSelectOptions } from "@/utils/array";
import { CoursesFilterEvent } from "@/data/eventbus";
import { filterCourses } from "@/utils/filter";

@Options({
    props: {
        regionId: {
            type: Number,
            required: true,
        },
    },
    components: {
        CourseFilter,
        RegionCoursesCard,
        CourseActions,
        CourseSpecialInfo,
    },
    watch: {
        regionId: {
            handler: 'regionIdChanged',
            immediate: true,
        }
    }
})
export default class RegionCourses extends Vue {
    regionId!: number

    loading = false
    loadingEnrollments = false

    coursesRaw: Course[] = []
    userCourseEnrollments: CourseEnrollment[] = []

    currentFilterEvent: CoursesFilterEvent|null = null

    days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
    dayCurrent = 0

    setActiveTab(index: number) {
        if( index < 0 ) {
            console.error('tab index too small', index);
        }
        if( index > (this.days.length - 1) ) {
            console.error('tab index too high', index);
        }
        this.dayCurrent = index;
    }

    get courses(): Course[] {
        if( !this.currentFilterEvent ) {
            return this.coursesRaw;
        }

        return filterCourses(this.coursesRaw, this.currentFilterEvent);
    }

    get coursesSorted(): Course[] {
        return this.courses.sort((a, b) => {
            if( a.weekday !== b.weekday ) {
                return a.weekday - b.weekday;
            }
            if( a.start_hours !== b.start_hours ) {
                return a.start_hours - b.start_hours;
            }
            return a.start_minutes - b.start_minutes;
        });
    }

    get coursesByWeekday(): Course[][] {
        return this.coursesSorted.reduce<Course[][]>((partition, course) => {
            /*if( typeof partition[course.weekday] === 'undefined' ) {
                partition[course.weekday] = [];
            }*/
            partition[course.weekday].push(course);
            return partition;
        }, this.days.map(_ => [])).filter(weekday => typeof weekday !== 'undefined'); // remove all days without entries
    }

    loadCourses() {
        this.loading = true;
        return RegionService.getRegionCourses(this.regionId as number).then(result => {
            return this.coursesRaw = result.data.data || [];
        }).finally(() => this.loading = false);
    }

    loadUserCourseEnrollments() {
        this.loadingEnrollments = true;
        return this.loadUserCourseEnrollmentsWithoutIndicator()
            .finally(() => this.loadingEnrollments = false);
    }
    loadUserCourseEnrollmentsWithoutIndicator() {
        return CourseService.getUserCourseEnrollments().then(result => {
            this.userCourseEnrollments = result.data.data || [];
        }).catch(error => {
            this.userCourseEnrollments = [];
            if( error.response.status !== 401 ) {
                EventBusService.$notification({
                    type: 'error',
                    message: 'Es ist ein Fehler beim Laden der Kursinformationen aufgetreten.',
                });
            }
        });
    }

    regionIdChanged() {
        this.coursesRaw = [];
        this.loadCourses().then(_ => {
            const firstDayIndexWithCourses = Math.min(...this.coursesRaw.map(course => course.weekday))
            this.dayCurrent = isFinite(firstDayIndexWithCourses) && firstDayIndexWithCourses > 0 ? firstDayIndexWithCourses : 0;
        });
        this.loadUserCourseEnrollments();
    }

    onEnrollmentStatusChange() {
        this.loadUserCourseEnrollmentsWithoutIndicator();
    }

    get filterCourseTypes() {
        return createSelectOptions(
            this.coursesRaw,
            (course: Course) => course.course_type.id,
            (course: Course) => course.course_type.name
        );
    }
    get filterLocations() {
        return createSelectOptions(
            this.coursesRaw.filter(course => typeof course.location !== 'undefined' && course.location),
            (course: Course) => course.location?.id || -1,
            (course: Course) => course.location?.name || ''
        );
    }

    filterSelectionChanged(event: CoursesFilterEvent): void {
        this.currentFilterEvent = {...event};
    }

    beforeMount() {
        this.$watch('coursesRaw', (courses: Course[]) => {
            const emptyClass = 'bf-courses-week-empty';
            if( courses.length > 0 ) {
                document.body.classList.remove(emptyClass);
            }
            else {
                document.body.classList.add(emptyClass);
            }
        }, { deep: true });
    }

    mounted() {
        // ???? dont listen to these login events because user.courses.enrollment:changed will get fired in a deeper component in the case of login/logout
        EventBusService.$on('user.login', this.loadUserCourseEnrollmentsWithoutIndicator);
        EventBusService.$on('user.logout', this.loadUserCourseEnrollmentsWithoutIndicator);
        EventBusService.$on('user.courses.enrollment:changed', this.loadUserCourseEnrollmentsWithoutIndicator);

        EventBusService.$on('region.courses.filter:changed', this.filterSelectionChanged);
    }
    beforeUnmount() {
        EventBusService.$off('user.login', this.loadUserCourseEnrollmentsWithoutIndicator);
        EventBusService.$off('user.logout', this.loadUserCourseEnrollmentsWithoutIndicator);
        EventBusService.$off('user.courses.enrollment:changed', this.loadUserCourseEnrollmentsWithoutIndicator);

        EventBusService.$off('region.courses.filter:changed', this.filterSelectionChanged);
    }
}
